// Formation AM Cyclomoteur — Module 3 · Sensibilisation (2 h)
// Séquence réglementaire obligatoire, en présence d'un parent/représentant légal si l'élève est mineur.
// Objectif : comprendre que les décisions AVANT et PENDANT le trajet déterminent le niveau de risque.

export const cours = {
  slug: 'permis-am-sensibilisation',
  title: 'Module 3 — Sensibilisation',
  subtitle: 'Comprendre les risques pour faire les bons choix',
  duree: '2 h · Présence parentale obligatoire (mineur)',
  mantra: 'SUR LA ROUTE, TON PREMIER ÉQUIPEMENT DE SÉCURITÉ, C\'EST TON COMPORTEMENT',
  intro: "Tu sais maintenant démarrer, ralentir, tourner et commencer à circuler. Mais savoir utiliser ton scooter ne suffit pas. Un accident n'arrive pas uniquement parce qu'un conducteur « ne sait pas conduire ». Pendant ces 2 heures, nous allons parler de toi, de tes futurs trajets et des situations que tu rencontreras réellement. L'objectif n'est pas de te faire peur : c'est de te donner des repères avant que tu te retrouves seul face à ces situations.",

  sections: [
    /* ================================================================
       01 — POURQUOI PARLER DES ACCIDENTS ?
    ================================================================ */
    {
      num: '01',
      kicker: 'Comprendre pour éviter',
      title: 'Pourquoi parler des accidents ?',
      lead: "Nous ne cherchons pas à te montrer des images choquantes ni à te dire « la moto, c'est dangereux ». Nous cherchons à comprendre comment un accident se construit.",
      type: 'list',
      list: [
        { label: 'COMMENT ?', desc: "Comment l'accident s'est-il construit ?" },
        { label: 'INDICES', desc: 'Quels indices étaient présents avant ?' },
        { label: 'DÉCISION', desc: 'Quelle décision aurait pu changer la situation ?' },
        { label: 'ANTICIPATION', desc: "Qu'est-ce que le conducteur aurait pu anticiper ?" },
      ],
      retenir: "COMPRENDRE COMMENT UN ACCIDENT SE CONSTRUIT, C'EST DÉJÀ APPRENDRE À L'ÉVITER.",
    },

    /* ================================================================
       02 — VULNÉRABILITÉ
    ================================================================ */
    {
      num: '02',
      kicker: 'Ton corps est exposé',
      title: 'Tu es plus vulnérable qu\'en voiture',
      lead: "Sur ton cyclomoteur, tu n'as ni carrosserie, ni ceinture, ni airbags autour de toi. Ton corps est directement exposé. Même à une allure qui peut sembler faible.",
      type: 'cards3',
      cards: [
        { title: 'OBSERVATION', desc: 'Tu vas chercher activement l\'information autour de toi.' },
        { title: 'ANTICIPATION', desc: 'Tu prévois ce qui pourrait arriver avant que ça arrive.' },
        { title: 'MARGE', desc: 'Tu conserves toujours un espace pour agir.' },
      ],
      retenir: "TU NE PEUX PAS EMPÊCHER LES ERREURS DES AUTRES. MAIS TU PEUX APPRENDRE À NE PAS LEUR LAISSER DÉCIDER DE CE QUI VA T'ARRIVER.",
    },

    /* ================================================================
       03 — LA VITESSE
    ================================================================ */
    {
      num: '03',
      kicker: '« Je ne roule qu\'à 45 km/h »',
      title: 'La bonne allure n\'est pas celle qui est autorisée',
      lead: "45 km/h n'est pas une petite vitesse lorsqu'un problème apparaît à quelques mètres devant toi. Plus ton allure augmente, moins tu disposes de temps pour analyser une situation proche.",
      type: 'list',
      list: [
        { label: 'STATIONNEMENT', desc: 'Une voiture qui sort brusquement.' },
        { label: 'PIÉTON', desc: 'Un enfant qui traverse sans regarder.' },
        { label: 'PORTIÈRE', desc: 'Une portière qui s\'ouvre côté circulation.' },
        { label: 'FREINAGE', desc: 'Le véhicule devant qui freine brutalement.' },
      ],
      retenir: "LA BONNE ALLURE, C'EST CELLE QUI TE LAISSE ENCORE UNE SOLUTION.",
    },

    /* ================================================================
       04 — TÉLÉPHONE
    ================================================================ */
    {
      num: '04',
      kicker: 'Distracteurs · téléphone',
      title: 'Ton téléphone peut attendre',
      lead: "Une notification, un message, un appel, une musique à changer. Quelques secondes semblent insignifiantes. Mais pendant ces secondes, la route, elle, continue.",
      type: 'cards3',
      cards: [
        { title: 'JE CONDUIS', desc: 'Je ne consulte pas mon téléphone.' },
        { title: 'JE M\'ARRÊTE', desc: 'Si urgence, je me gare dans un endroit sûr.' },
        { title: 'J\'ATTENDS', desc: 'Le message attendra. La route, non.' },
      ],
      retenir: 'JE CONDUIS → JE NE CONSULTE PAS MON TÉLÉPHONE.',
    },

    /* ================================================================
       05 — ALCOOL / SUBSTANCES
    ================================================================ */
    {
      num: '05',
      kicker: 'Alcool · cannabis · substances',
      title: 'Ton cerveau change avant que tu t\'en rendes compte',
      lead: "Conduire demande en permanence : observer, comprendre, décider, coordonner, réagir. L'alcool et les substances psychoactives perturbent précisément ces capacités. Le piège ? Tu peux te sentir capable alors que tes capacités sont déjà altérées.",
      type: 'list',
      list: [
        { label: 'TEMPS DE RÉACTION', desc: 'Diminué de façon invisible.' },
        { label: 'ATTENTION', desc: 'Rétrécie sur ce qui est proche.' },
        { label: 'PERCEPTION', desc: 'Distorsion des distances et des vitesses.' },
        { label: 'COORDINATION', desc: 'Gestes moins précis, équilibre fragilisé.' },
        { label: 'DÉCISION', desc: 'Choix plus lents et moins pertinents.' },
        { label: 'ÉVALUATION', desc: 'Sous-estimation du risque réel.' },
      ],
      retenir: 'SI TU N\'ES PAS EN ÉTAT DE CONDUIRE, TU NE CONDUIS PAS.',
    },

    /* ================================================================
       06 — FATIGUE
    ================================================================ */
    {
      num: '06',
      kicker: 'Fatigue et état émotionnel',
      title: 'La fatigue est aussi un risque',
      lead: "Ton scooter fonctionne parfaitement. Ton casque est attaché. Pourtant, quelque chose a changé : ton conducteur. La fatigue diminue ton attention, ta capacité d'anticipation, ta rapidité de réaction et la qualité de tes décisions.",
      type: 'list',
      list: [
        { label: 'ATTENTION', desc: 'Difficulté à rester concentré longtemps.' },
        { label: 'ANTICIPATION', desc: 'On voit les événements trop tard.' },
        { label: 'RÉACTION', desc: 'Le geste arrive avec retard.' },
        { label: 'DÉCISIONS', desc: 'Plus impulsives, moins pesées.' },
      ],
      retenir: '« EST-CE QUE MOI, JE SUIS EN ÉTAT DE ROULER ? »',
    },

    /* ================================================================
       07 — PRESSION DU GROUPE
    ================================================================ */
    {
      num: '07',
      kicker: 'Pression sociale',
      title: 'Tes copains ne conduisent pas à ta place',
      lead: "Tu roules avec des amis. Ils accélèrent. Tu prends du retard. Quelqu'un te fait signe de suivre. C'est ici qu'intervient un risque très particulier : la pression du groupe.",
      type: 'cards3',
      cards: [
        { title: 'ROULER PLUS VITE', desc: 'Tenté de dépasser ton niveau réel.' },
        { title: 'SUIVRE', desc: 'Faire ce que tu n\'aurais jamais choisi seul.' },
        { title: 'PROUVER', desc: 'Vouloir montrer que tu es capable.' },
      ],
      retenir: 'CELUI QUI PREND LA DÉCISION EST AUSSI CELUI QUI EN SUBIRA LES CONSÉQUENCES. TU ROULES À TON NIVEAU, PAS À CELUI DE CELUI QUI EST DEVANT.',
    },

    /* ================================================================
       08 — ÉQUIPEMENT
    ================================================================ */
    {
      num: '08',
      kicker: 'Équipement complet',
      title: 'Ton équipement n\'est pas fait pour le parking',
      lead: "Casque, gants, blouson, pantalon adapté, chaussures protectrices. Pourquoi s'équiper pour seulement quelques kilomètres ? Parce qu'un accident ne te demande pas « tu vas loin ? ». L'habitude peut diminuer ta vigilance.",
      type: 'list',
      list: [
        { label: 'CASQUE', desc: 'Ajusté et attaché correctement.' },
        { label: 'GANTS', desc: 'Protection des mains en cas de chute.' },
        { label: 'BLOUSON', desc: 'Protection du buste et des bras.' },
        { label: 'PANTALON', desc: 'Adapté, jamais un short en été.' },
        { label: 'CHAUSSURES', desc: 'Fermées, montantes de préférence.' },
        { label: 'HABITUDE', desc: 'Même pour l\'école, même pour 500 m.' },
      ],
      retenir: 'TON ÉQUIPEMENT DOIT DEVENIR UNE ROUTINE, PAS UNE EXCEPTION.',
    },

    /* ================================================================
       09 — PASSAGER
    ================================================================ */
    {
      num: '09',
      kicker: 'Transport d\'un passager',
      title: 'Être passager change aussi le comportement du deux-roues',
      lead: "Lorsque le véhicule et les conditions réglementaires permettent de transporter un passager, la machine se comporte différemment. Tu deviens responsable de ta conduite avec quelqu'un d'autre derrière toi.",
      type: 'list',
      list: [
        { label: 'POIDS', desc: 'Plus lourd, moins vif.' },
        { label: 'ÉQUILIBRE', desc: 'Modifié dès l\'arrêt et au démarrage.' },
        { label: 'ACCÉLÉRATION', desc: 'Reprises plus lentes qu\'à vide.' },
        { label: 'FREINAGE', desc: 'Distance de freinage allongée.' },
        { label: 'MOUVEMENTS', desc: 'Le passager peut bouger de façon imprévue.' },
        { label: 'CONSIGNES', desc: 'Explique-lui comment se comporter avant de partir.' },
      ],
      retenir: 'TU ADAPTES TA CONDUITE — PAS SEULEMENT TA MACHINE.',
    },

    /* ================================================================
       10 — ÉTAT DU VÉHICULE
    ================================================================ */
    {
      num: '10',
      kicker: 'Vérifications avant départ',
      title: 'Ton scooter doit aussi être en état de rouler',
      lead: "La sécurité ne commence pas quand tu démarres. Elle commence avant. Quelques secondes suffisent pour détecter ce qui pourrait poser problème.",
      type: 'list',
      list: [
        { label: 'PNEUMATIQUES', desc: 'Pression et usure visuelle.' },
        { label: 'ÉCLAIRAGE', desc: 'Feux avant, arrière, stop.' },
        { label: 'RÉTROVISEURS', desc: 'Bien réglés et propres.' },
        { label: 'FREINS', desc: 'Comportement normal, pas de bruit anormal.' },
        { label: 'ÉLÉMENTS', desc: 'Rien de cassé, rien qui bouge.' },
      ],
      retenir: 'JE M\'ÉQUIPE → JE REGARDE MON VÉHICULE → JE PARS.',
    },

    /* ================================================================
       11 — MODIFICATIONS
    ================================================================ */
    {
      num: '11',
      kicker: 'Kit, débridage, échappement',
      title: 'Modifier son cyclomoteur n\'est pas anodin',
      lead: "Un échappement, un élément mécanique, un dispositif pour « aller plus vite ». Une modification peut sembler attractive. Mais elle peut changer le comportement du véhicule, sa conformité, sa fiabilité et ton exposition au risque.",
      type: 'cards3',
      cards: [
        { title: 'PERSONNALISER', desc: 'Autocollants, esthétique — sans modifier la mécanique.' },
        { title: 'MODIFIER', desc: 'Débridage, kit — le véhicule n\'est plus conforme.' },
        { title: 'CONSÉQUENCES', desc: 'Assurance nulle, sanctions, comportement imprévisible.' },
      ],
      retenir: 'LA SÉCURITÉ, C\'EST AUSSI UTILISER UN VÉHICULE CONFORME ET ENTRETENU.',
    },

    /* ================================================================
       12 — SCÉNARIOS INTERACTIFS "Que ferais-tu ?"
    ================================================================ */
    {
      num: '12',
      kicker: 'Que ferais-tu ?',
      title: 'Apprends à reconnaître une situation qui se dégrade',
      lead: "Pour chaque situation, la même méthode : QU'EST-CE QUI CHANGE ? → QUEL EST LE RISQUE ? → QUELLE EST MA SOLUTION ?",
      type: 'scenarios',
      scenarios: [
        {
          id: 'pluie',
          title: 'Il commence à pleuvoir',
          question: 'Que change-t-il pour toi ?',
          changes: ['Adhérence des pneus', 'Visibilité (visière, autres)', 'Distance de freinage', 'Comportement des autres usagers'],
          solution: "J'allume mes feux, je ralentis, j'augmente les distances, je m'arrête si nécessaire pour bien voir.",
        },
        {
          id: 'retard',
          title: 'Tu es en retard',
          question: 'Que risques-tu de modifier dans ta conduite ?',
          changes: ['Allure trop élevée', 'Distances trop courtes', 'Décisions précipitées', 'Attention rétrécie'],
          solution: "Je préviens si possible. J'accepte le retard plutôt que le risque. Mieux vaut arriver en retard qu'en ambulance.",
        },
        {
          id: 'copains',
          title: 'Ton ami roule beaucoup plus vite devant',
          question: 'Que fais-tu ?',
          changes: ['Tentation de suivre', 'Perte du niveau maîtrisé', 'Décision sous pression'],
          solution: "Je garde mon allure. On se retrouve à l'arrivée. Je ne roule pas au niveau de quelqu'un d'autre.",
        },
        {
          id: 'telephone',
          title: 'Ton téléphone sonne dans ta poche',
          question: 'Comment tu réagis ?',
          changes: ['Distraction sonore', 'Envie de vérifier', 'Perte d\'attention visuelle'],
          solution: "Je laisse sonner. Si vraiment nécessaire, je m'arrête en sécurité pour répondre. Jamais en roulant.",
        },
        {
          id: 'fatigue',
          title: 'Tu te sens très fatigué avant de repartir',
          question: 'Quelle est ta décision ?',
          changes: ['Réactions ralenties', 'Décisions moins bonnes', 'Attention flottante'],
          solution: "Plan B : appeler un parent, transport en commun, attendre. Renoncer à un trajet est une bonne décision.",
        },
      ],
      retenir: 'QU\'EST-CE QUI CHANGE ? → QUEL EST LE RISQUE ? → QUELLE EST MA SOLUTION ?',
    },

    /* ================================================================
       13 — PARENTS / REPRÉSENTANT LÉGAL
    ================================================================ */
    {
      num: '13',
      kicker: 'Échange famille',
      title: 'Tes parents entrent dans la discussion',
      lead: "Si tu es mineur, cette séquence est aussi l'occasion d'échanger avec ton parent ou représentant légal. L'objectif n'est pas de transformer ce moment en leçon de morale : c'est de préparer les situations avant qu'elles arrivent.",
      type: 'list',
      list: [
        { label: 'OÙ ?', desc: 'Où vas-tu rouler ? Quels trajets habituels ?' },
        { label: 'QUAND ?', desc: 'À quels horaires ? Le soir ? La nuit ?' },
        { label: 'ÉQUIPEMENT', desc: 'Quel équipement utiliseras-tu à chaque sortie ?' },
        { label: 'PLAN B', desc: 'Comment réagir si tu ne peux pas rentrer avec ton cyclomoteur ?' },
        { label: 'CONTACT', desc: 'Qui peux-tu appeler à tout moment sans jugement ?' },
        { label: 'RÈGLES', desc: 'Quelles règles doivent être claires dès maintenant ?' },
      ],
      retenir: "PRÉPARER LES SITUATIONS AVANT QU'ELLES ARRIVENT.",
    },

    /* ================================================================
       14 — PLAN B
    ================================================================ */
    {
      num: '14',
      kicker: 'Renoncer, c\'est décider',
      title: 'Ton plan B fait partie de ta sécurité',
      lead: "Tu dois toujours pouvoir décider : « je ne prends pas mon scooter ». Consommé quelque chose, trop fatigué, très mauvaise météo, véhicule qui a un problème, ou juste : tu ne te sens pas capable.",
      type: 'cards3',
      cards: [
        { title: 'PARENT', desc: 'Appeler quelqu\'un qui peut venir te chercher.' },
        { title: 'TRANSPORT', desc: 'Bus, tram, taxi, VTC, à pied.' },
        { title: 'ATTENDRE', desc: 'Rester sur place, dormir si possible.' },
      ],
      retenir: 'RENONCER À UN TRAJET PEUT ÊTRE UNE EXCELLENTE DÉCISION DE CONDUCTEUR.',
    },

    /* ================================================================
       15 — ENGAGEMENT PERSONNEL
    ================================================================ */
    {
      num: '15',
      kicker: 'Tes règles à toi',
      title: 'Ton engagement personnel',
      lead: "À la fin de cette séquence, je veux que tu choisisses toi-même tes règles. Pas les miennes — LES TIENNES. Après la formation, l'enseignant ne sera plus derrière toi.",
      type: 'engagement',
      engagements: [
        'Je ne regarde jamais mon téléphone en roulant.',
        'Je mets mon équipement complet, même pour un trajet court.',
        'Je ne roule pas au-dessus de mon niveau.',
        'Je ne conduis pas si je ne suis pas en état.',
        'Je conserve toujours une solution de retour (plan B).',
        'Je ne prends pas un risque uniquement pour impressionner quelqu\'un.',
      ],
      retenir: 'APRÈS LA FORMATION, L\'ENSEIGNANT NE SERA PLUS DERRIÈRE TOI.',
    },
  ],

  /* ==================================================================
     BILAN FINAL — checklist réglementaire
  ================================================================== */
  bilan: {
    title: 'Ce que tu dois pouvoir expliquer avec tes propres mots',
    intro: 'À la fin des 2 heures, tu dois pouvoir expliquer :',
    items: [
      'Pourquoi un conducteur de cyclomoteur est vulnérable.',
      'Pourquoi l\'allure doit être adaptée à la situation.',
      'Pourquoi le téléphone détourne ton attention.',
      'Pourquoi alcool et substances sont incompatibles avec une conduite sûre.',
      'Pourquoi la fatigue influence ta conduite.',
      'Comment résister à la pression du groupe.',
      'Pourquoi l\'équipement reste indispensable sur un trajet court.',
      'Pourquoi l\'état du véhicule compte.',
      'Pourquoi certaines modifications du cyclomoteur posent problème.',
      'Comment identifier une situation qui se dégrade.',
      'Pourquoi avoir un plan B est une compétence de conducteur.',
    ],
  },

  /* ==================================================================
     MESSAGE FINAL
  ================================================================== */
  message: {
    title: 'Le message final',
    body: "Tu peux apprendre à manier parfaitement ton scooter. Tu peux connaître toutes les priorités. Tu peux réussir tous les exercices de la formation. Mais une seule mauvaise décision peut suffire à supprimer toutes les marges que tu avais construites.",
    punchline: 'CONDUIRE, CE N\'EST PAS SEULEMENT SAVOIR UTILISER UN DEUX-ROUES. C\'EST SAVOIR QUAND ROULER, COMMENT ROULER… ET PARFOIS SAVOIR NE PAS ROULER.',
  },
};

export default cours;
